import { useEffect, useState } from "react";
import { AdminLayout } from "../components/AdminLayout";
import axios from "axios";
import { getAuthHeader } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function DiscountsPage() {
  const [discounts, setDiscounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editDiscount, setEditDiscount] = useState(false);
  const [creditSlabs, setCreditSlabs] = useState([]);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [editingSlab, setEditingSlab] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    type: "percentage",
    value: "",
    applied_on: "order",
    is_active: true,
  });
  const [creditForm, setCreditForm] = useState({
    min_value: "",
    max_value: "",
    credit_percent: "",
  });

  useEffect(() => {
    fetchDiscounts();
    fetchCreditSlabs();
  }, []);

  const fetchDiscounts = async () => {
    try {
      const response = await axios.get(`${API}/discounts`, {
        headers: getAuthHeader(),
      });
      setDiscounts(response.data);
    } catch (error) {
      console.error("Error fetching discounts:", error);
      toast.error("Failed to fetch discounts");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editDiscount) {
        await axios.patch(
          `${API}/discounts/${editDiscount.id}`,
          {
            ...formData,
            value: parseFloat(formData.value),
          },
          { headers: getAuthHeader() }
        );
        toast.success("Discount updated successfully");
      } else {
        await axios.post(
          `${API}/discounts`,
          {
            ...formData,
            value: parseFloat(formData.value),
          },
          { headers: getAuthHeader() }
        );
        toast.success("Discount created successfully");
      }
      setOpen(false);
      setFormData({
        name: "",
        type: "percentage",
        value: "",
        applied_on: "order",
        is_active: true,
      });
      fetchDiscounts();
    } catch (error) {
      console.error("Error creating discount:", error);
      toast.error("Failed to create discount");
    }
  };

  const fetchCreditSlabs = async () => {
    try {
      const response = await axios.get(`${API}/credit-slabs`, {
        headers: getAuthHeader(),
      });
      setCreditSlabs(response.data);
    } catch (error) {
      console.error("Error fetching credit slabs:", error);
      toast.error("Failed to load credit slabs");
    }
  };

  const handleEditSlab = (slab) => {
    setCreditForm({
      min_value: slab.min_value,
      max_value: slab.max_value,
      credit_percent: slab.credit_percent,
    });
    setEditingSlab(slab.id);
    setCreditModalOpen(true);
  };

  const handleDeleteSlab = async (id) => {
    if (!window.confirm("Are you sure you want to delete this credit slab?"))
      return;

    try {
      await axios.delete(`${API}/credit-slabs/${id}`, {
        headers: getAuthHeader(),
      });
      toast.success("Credit slab deleted");
      fetchCreditSlabs();
    } catch (error) {
      console.error("Error deleting slab:", error);
      toast.error("Failed to delete credit slab");
    }
  };

  const openEditDiscount = (section) => {
    setEditDiscount(section);
    setFormData({
      name: section.name,
      value: parseFloat(section.value),
      type: "percentage",
      applied_on: "order",
      is_active: true,
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this discount?")) return;
    try {
      await axios.delete(`${API}/discounts/${id}`, {
        headers: getAuthHeader(),
      });
      toast.success("Discount deleted");
      fetchDiscounts();
    } catch (error) {
      console.error("Error deleting discount:", error);
      toast.error("Failed to delete discount");
    }
  };

    const handleSaveSlab = async () => {
    if (
      !creditForm.min_value ||
      !creditForm.max_value ||
      !creditForm.credit_percent
    ) {
      toast.error("Please fill all fields");
      return;
    }

    const payload = {
      min_value: Number(creditForm.min_value),
      max_value: Number(creditForm.max_value),
      credit_percent: Number(creditForm.credit_percent),
    };

    try {
      if (editingSlab) {
        // Update existing slab
        await axios.put(`${API}/credit-slabs/${editingSlab}`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Credit slab updated");
      } else {
        // Create new slab
        await axios.post(`${API}/credit-slabs`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Credit slab created");
      }

      setCreditModalOpen(false);
      fetchCreditSlabs();
    } catch (error) {
      console.error("Error saving slab:", error);
      toast.error("Failed to save credit slab");
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">
              Discounts and Promotions
            </h1>
            <p className="text-gray-600">Manage offers and discounts</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-discount-button"
                className="bg-gradient-to-r from-purple-500 to-pink-500"
              >
                <Plus size={20} className="mr-2" />
                Add Discount
              </Button>
            </DialogTrigger>
            <Button
              onClick={() => {
                setCreditForm({
                  min_value: "",
                  max_value: "",
                  credit_percent: "",
                });
                setEditingSlab(null);
                setCreditModalOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus size={16} className="mr-2" />
              Add Credit Slab
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {" "}
                  {editDiscount ? "Edit Discount" : "Create Discount"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Discount Name</Label>
                  <Input
                    id="name"
                    data-testid="discount-name-input"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="type">Type</Label>
                  <select
                    id="type"
                    data-testid="discount-type-select"
                    className="w-full p-2 border rounded-md"
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value })
                    }
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="value">Value</Label>
                  <Input
                    id="value"
                    data-testid="discount-value-input"
                    type="number"
                    step="0.01"
                    value={formData.value}
                    onChange={(e) =>
                      setFormData({ ...formData, value: e.target.value })
                    }
                    required
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="submit-discount-button"
                  className="w-full"
                >
                  {editDiscount ? "Updated" : "Create"} Discount
                </Button>
              </form>
            </DialogContent>
            {/* Credit Slab Modal */}
            <Dialog open={creditModalOpen} onOpenChange={setCreditModalOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    {editingSlab ? "Edit Credit Slab" : "Add Credit Slab"}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  <div>
                    <Label>Min Value *</Label>
                    <Input
                      type="number"
                      value={creditForm.min_value}
                      onChange={(e) =>
                        setCreditForm({
                          ...creditForm,
                          min_value: e.target.value,
                        })
                      }
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <Label>Max Value *</Label>
                    <Input
                      type="number"
                      value={creditForm.max_value}
                      onChange={(e) =>
                        setCreditForm({
                          ...creditForm,
                          max_value: e.target.value,
                        })
                      }
                      placeholder="50"
                    />
                  </div>

                  <div>
                    <Label>Credit Percent (%) *</Label>
                    <Input
                      type="number"
                      value={creditForm.credit_percent}
                      onChange={(e) =>
                        setCreditForm({
                          ...creditForm,
                          credit_percent: e.target.value,
                        })
                      }
                      placeholder="10"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setCreditModalOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={handleSaveSlab}
                    >
                      {editingSlab ? "Update" : "Create"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {discounts.map((discount) => (
            <Card
              key={discount.id}
              className="border-0 shadow-lg"
              data-testid="discount-card"
            >
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  <span>{discount.name}</span>
                  <div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDiscount(discount)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit size={14} className="text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(discount.id)}
                      data-testid="delete-discount-button"
                    >
                      <Trash2 size={16} className="text-red-600" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-semibold capitalize">
                      {discount.type}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Value:</span>
                    <span className="font-bold text-blue-600">
                      {discount.type === "percentage"
                        ? `${discount.value}%`
                        : `$${discount.value}`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        discount.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {discount.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Credit Slabs List */}
        <div className="mt-10">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Credit Slabs
          </h2>

          {creditSlabs.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-gray-500">
                No credit slabs added yet.
              </CardContent>
            </Card>
          ) : (
            creditSlabs.map((slab) => (
              <Card key={slab.id} className="mb-4">
                <CardContent className="p-4 flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-700">
                      Min: <b>{slab.min_value}</b> | Max:{" "}
                      <b>{slab.max_value}</b>
                    </p>
                    <p className="text-lg font-semibold text-blue-700">
                      {slab.credit_percent}% Credit
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditSlab(slab)}
                    >
                      <Edit size={16} />
                    </Button>

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteSlab(slab.id)}
                    >
                      <Trash2 size={16} className="text-red-600" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
